import { useEffect, useId, useState } from "react";
import { SITE } from "../data/site";
import styles from "./Location.module.css";

const OSM_ATTRIBUTION_URL = "https://www.openstreetmap.org/copyright";

export function Location() {
  const [mapLoaded, setMapLoaded] = useState(false);
  const headingId = useId();
  const disclosureId = useId();

  useEffect(() => {
    // If the user has already opted in (e.g. via a session-level preference), the
    // click handler below will set the flag; nothing else needed here.
  }, []);

  const onActivateMap = () => setMapLoaded(true);

  return (
    <section id="visit" className={styles.section} aria-labelledby={headingId}>
      <div className={`container-wide ${styles.grid}`}>
        <figure className={styles.map} data-reveal>
          {mapLoaded ? (
            <>
              <iframe
                src={SITE.mapEmbed}
                title={`Map showing ${SITE.name} studio at ${SITE.addressLine}`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <figcaption className={`mediaBadge ${styles.mapBadge}`}>
                <span className={`mediaBadgeDot ${styles.dot}`} aria-hidden="true" />
                {SITE.city}
              </figcaption>
              <a
                className={styles.attribution}
                href={OSM_ATTRIBUTION_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                &copy; OpenStreetMap contributors
              </a>
            </>
          ) : (
            <button
              type="button"
              className={styles.mapPlaceholder}
              onClick={onActivateMap}
              aria-describedby={disclosureId}
            >
              <span className={styles.mapPlaceholderInner}>
                <span className={styles.mapPlaceholderIcon} aria-hidden="true">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 22s7-7.5 7-13a7 7 0 0 0-14 0c0 5.5 7 13 7 13Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </span>
                <span className={styles.mapPlaceholderLabel}>Show map of the studio</span>
                <span className={styles.mapPlaceholderAddress}>{SITE.addressLine}</span>
              </span>
              <span id={disclosureId} className={styles.mapPlaceholderDisclosure}>
                Loads OpenStreetMap. Your IP and browser are sent to openstreetmap.org.
              </span>
            </button>
          )}
        </figure>

        <div className={styles.text}>
          <h2 id={headingId} className={styles.heading}>
            Hermosa Beach, <em>by appointment only.</em>
          </h2>
          <p className={styles.body}>
            The studio is a private one-chair space. Quiet, calm, and built for the kind of
            consultation you actually want before a color service.
          </p>

          <dl className={styles.details}>
            <div className={styles.row}>
              <dt className={styles.label}>Address</dt>
              <dd className={styles.value}>{SITE.addressLine}</dd>
            </div>
            <div className={styles.row}>
              <dt className={styles.label}>Hours</dt>
              <dd className={styles.value}>By appointment, Tuesday through Saturday</dd>
            </div>
            <div className={styles.row}>
              <dt className={styles.label}>Phone</dt>
              <dd className={styles.value}>
                <a href={`tel:${SITE.phoneHref}`}>{SITE.phone}</a>
              </dd>
            </div>
            <div className={styles.row}>
              <dt className={styles.label}>Instagram</dt>
              <dd className={styles.value}>
                <a href={SITE.instagramHref} target="_blank" rel="noopener noreferrer">
                  {SITE.instagram}
                </a>
              </dd>
            </div>
          </dl>

          <div className={styles.actions}>
            <a
              className="btn btn--primary btn--lg"
              href={SITE.mapHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              Get directions
              <span aria-hidden="true">{"\u2197"}</span>
            </a>
            <a className="btn btn--secondary btn--lg" href={`tel:${SITE.phoneHref}`}>
              Call {SITE.phone}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}